import { Component, computed, effect, inject, Injector, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { Dispatcher } from '@ngrx/signals/events';
import { AssetData, Network, ReviewData } from '@models/online-reputation.models';
import { ReviewsStore } from '@core/stores/reviews.store';

import {
  createReplyTemplate,
  deleteReplyTemplate,
  generateComment,
  loadReplyTemplates,
  submitReviewReply,
  updateReplyTemplate,
  clearReviews,
  setReplyFormUi,
  clearReplyFormLocalErrors
} from '@core/events/reviews.events';
import {MatTooltip} from '@angular/material/tooltip';
import {ButtonComponent} from '@shared/components/ui/button/button.component';

export type ReplyFormDialogData = {
  review: ReviewData;
  network: Network;
};

export type ReplyFormDialogResult = {
  submitted: boolean;
};

type TemplateMode = 'custom' | 'selected' | 'create' | 'edit';
type ActiveTab = 'select' | 'manage';
type TemplateAction = 'create' | 'update' | 'delete';

@Component({
  selector: 'app-reply-form',
  templateUrl: './reply-form.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    MatProgressBarModule,
    MatTabsModule,
    MatTooltip,
    ButtonComponent
  ]
})
export class ReplyFormComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly dispatcher = inject(Dispatcher);
  private readonly reviewsStore = inject(ReviewsStore);
  private readonly dialogRef = inject<MatDialogRef<ReplyFormComponent, ReplyFormDialogResult>>(MatDialogRef);

  readonly dialogData = inject<ReplyFormDialogData>(MAT_DIALOG_DATA);
  readonly form = this.fb.nonNullable.group({
    templateId: [''],
    title: [''],
    reply: ['', [Validators.required, Validators.maxLength(5000)]]
  });

  readonly loadingTemplates = this.reviewsStore.loadingTemplates;

  // UI state is managed in the ReviewsStore to avoid ExpressionChangedAfterItHasBeenChecked errors.

  // Labels
  ownGroupLabel = 'My Templates';
  generalGroupLabel = 'General Templates';

  // Derived/computed signals from the ReviewsStore
  readonly templates = computed(() => this.reviewsStore.templates());
  readonly savingTemplate = computed(() => this.reviewsStore.savingTemplate());
  readonly deletingTemplate = computed(() => this.reviewsStore.deletingTemplate());
  readonly submittingReply = computed(() => this.reviewsStore.submittingReply());
  readonly generatingComment = computed(() => this.reviewsStore.generatingComment());
  // Merge store errors with reply-form local overrides stored in ReviewsStore: local takes precedence when set
  // use 'as any' for newly-added replyForm local signals to avoid tight coupling with store typing here
  readonly templateError = computed(() => (this.reviewsStore as any).replyFormLocalTemplateError?.() || (this.reviewsStore.templateError() ?? ''));
  readonly submitError = computed(() => (this.reviewsStore as any).replyFormLocalSubmitError?.() || (this.reviewsStore.submitError() ?? ''));
  readonly generateCommentError = computed(() => (this.reviewsStore as any).replyFormLocalGenerateCommentError?.() || (this.reviewsStore.generateCommentError() ?? ''));

  // Accessors that read/write UI state from/to the ReviewsStore
  get activeTab(): ActiveTab { return ((this.reviewsStore as any).replyFormActiveTab() as ActiveTab) ?? 'select'; }
  set activeTab(v: ActiveTab) { this.dispatcher.dispatch(setReplyFormUi({ activeTab: v })); }

  get templateMode(): TemplateMode { return ((this.reviewsStore as any).replyFormTemplateMode() as TemplateMode) ?? 'custom'; }
  set templateMode(v: TemplateMode) { this.dispatcher.dispatch(setReplyFormUi({ templateMode: v })); }

  get templateSuccessMessage(): string { return (this.reviewsStore as any).replyFormTemplateSuccessMessage() ?? ''; }
  set templateSuccessMessage(v: string) { this.dispatcher.dispatch(setReplyFormUi({ templateSuccessMessage: v })); }

  private preferredTemplateId?: string;
  private seenTemplateMutationVersion = 0;
  private seenReplySubmitVersion = 0;
  private pendingTemplateAction?: TemplateAction;
  private successMessageTimeoutId?: ReturnType<typeof setTimeout>;

  private readonly injector = inject(Injector);

  constructor() {
    this.seenTemplateMutationVersion = this.reviewsStore.templateMutationVersion();
    this.seenReplySubmitVersion = this.reviewsStore.replySubmitVersion();
  }

  get review(): ReviewData {
    return this.dialogData.review;
  }

  get hasReviewComment(): boolean {
    return !!this.review?.comment?.trim();
  }

  get selectedTemplate(): AssetData | undefined {
    return this.templates().find((template: AssetData) => template.id === this.form.controls.templateId.value);
  }

  get showTitleField(): boolean {
    return this.templateMode === 'create' || this.templateMode === 'edit';
  }

  get canManageSelectedTemplate(): boolean {
    return !!this.selectedTemplate?.id && !this.savingTemplate() && !this.deletingTemplate();
  }

  get canEditSelectedTemplate(): boolean {
    return this.isOwnTemplate(this.selectedTemplate) && !this.savingTemplate() && !this.deletingTemplate();
  }

  get canDeleteSelectedTemplate(): boolean {
    return this.isOwnTemplate(this.selectedTemplate) && !this.savingTemplate() && !this.deletingTemplate();
  }

  get groupedTemplates(): any{

    const groupedAssets: { title: string; value: AssetData[] }[] = [];
    const groupMap: { [key: string]: AssetData[] } = {};
    const entityId = this.review.locationId || '';
    this.templates()
      .filter((template: AssetData) => template?.entityIds == undefined || template.entityIds?.includes(entityId)) // Filter out templates without a name
      .forEach((asset: AssetData) => {
        const group = this.isOwnTemplate(asset) ? this.ownGroupLabel : this.generalGroupLabel;
        if (!groupMap[group]) groupMap[group] = [];
        groupMap[group].push(asset);
      });

    for (const group in groupMap) {
      groupedAssets.push({ title: group, value: groupMap[group] });
    }

    return groupedAssets;
  }

  ngOnInit(): void {
    this.bindStoreState();
    this.loadTemplates();
  }

  ngOnDestroy(): void {
    this.clearTemplateSuccessMessage();
  }

  loadTemplates(preferredTemplateId?: string): void {
    if (preferredTemplateId) {
      this.preferredTemplateId = preferredTemplateId;
    }

    this.dispatcher.dispatch(loadReplyTemplates({
      language: this.review.language,
      network: this.dialogData.network,
      entityIds: this.review.locationId ? [this.review.locationId] : [],
      limit: 100
    }));
  }

  onTabChange(selectedIndex: number): void {
    this.activeTab = selectedIndex === 0 ? 'select' : 'manage';

    if (this.activeTab === 'select') {
      this.templateMode = this.form.controls.templateId.value ? 'selected' : 'custom';
      return;
    }

    this.templateMode = this.form.controls.templateId.value ? 'edit' : 'create';
  }

  onTemplateSelection(templateId: string): void {
    this.form.controls.templateId.setValue(templateId);

    if (!templateId) {
      this.templateMode = 'custom';
      this.form.controls.title.setValue('');
      return;
    }

    const template = this.templates().find((item: AssetData) => item.id === templateId);
    if (!template) {
      this.templateMode = 'custom';
      return;
    }

    this.templateMode = 'selected';
    this.patchTemplateIntoForm(template);
  }

  onTemplateSelectionForManage(templateId: string): void {
    this.form.controls.templateId.setValue(templateId);

    if (!templateId) {
      this.templateMode = 'create';
      this.form.controls.title.setValue('');
      return;
    }

    const template = this.templates().find((item: AssetData) => item.id === templateId);
    if (!template) {
      this.templateMode = 'create';
      return;
    }

    this.activeTab = 'manage';
    this.templateMode = 'edit';
    this.dispatcher.dispatch(setReplyFormUi({ localTemplateError: '' }));
    // Patch the form in the next microtask so store-side UI updates do not
    // conflict with immediate template-bound changes during the same CD cycle.
    Promise.resolve().then(() => this.patchTemplateIntoForm(template));
  }

  startCreatingTemplate(): void {
    this.activeTab = 'manage';
    this.templateMode = 'create';
    this.dispatcher.dispatch(setReplyFormUi({ localTemplateError: '' }));
    // Defer clearing success message and patching the form to avoid
    // flipping template-bound values in the current change-detection pass.
    Promise.resolve().then(() => {
      this.clearTemplateSuccessMessage();
      this.form.patchValue({
        templateId: '',
        title: ''
      });
    });
  }

  startEditingTemplate(): void {
    const template = this.selectedTemplate;
    if (!this.canEditSelectedTemplate || !template) {
      return;
    }

    this.activeTab = 'manage';
    this.templateMode = 'edit';
    this.dispatcher.dispatch(setReplyFormUi({ localTemplateError: '' }));
    // Defer UI updates to next microtask to avoid ExpressionChangedAfterItHasBeenChecked
    Promise.resolve().then(() => {
      this.clearTemplateSuccessMessage();
      this.patchTemplateIntoForm(template);
    });
  }

  saveTemplate(): void {
    const title = this.form.controls.title.value.trim();
    const reply = this.form.controls.reply.value.trim();
    const entityId = this.review.locationId || '';

    if (!title || !reply || !entityId) {
      this.form.controls.title.markAsTouched();
      this.form.controls.reply.markAsTouched();
      return;
    }

    const request = {
      name: title,
      content: reply,
      entityIds: [entityId],
      language: this.review.language,
    };

    if (this.templateMode === 'edit' && this.selectedTemplate?.id) {
      this.pendingTemplateAction = 'update';
      this.dispatcher.dispatch(updateReplyTemplate({
        id: this.selectedTemplate.id,
        request
      }));
      return;
    }

    this.pendingTemplateAction = 'create';
    this.dispatcher.dispatch(createReplyTemplate(request));
  }

  deleteTemplate(): void {
    const templateId = this.selectedTemplate?.id;
    if (!this.canDeleteSelectedTemplate || !templateId) {
      return;
    }

    this.pendingTemplateAction = 'delete';
    this.dispatcher.dispatch(deleteReplyTemplate({ id: templateId }));
  }

  submitReply(): void {
    const reviewId = this.review.id?.trim();
    const content = this.form.controls.reply.value.trim();

    if (!reviewId) {
        this.dispatcher.dispatch(setReplyFormUi({ localSubmitError: 'This review does not have a reply target id yet.' }));
      return;
    }

    if (!content) {
      this.form.controls.reply.markAsTouched();
      return;
    }

    // clear any previous local submit error and submit
    this.dispatcher.dispatch(setReplyFormUi({ localSubmitError: '' }));
    this.dispatcher.dispatch(submitReviewReply({ reviewId, content }));
  }

  close(): void {

    // Reset the form fields back to their initial/empty values
    this.form.reset({
      templateId: '',
      title: '',
      reply: ''
    });

    // Clear form validation/touched state
    this.form.markAsPristine();
    this.form.markAsUntouched();

    // Reset local UI state so the next open starts fresh
    this.templateMode = 'custom';
    this.activeTab = 'select';
    this.preferredTemplateId = undefined;

    // Clear any local messages or timeouts
    this.clearTemplateSuccessMessage();
    // Clear any reply-form local errors stored in the ReviewsStore
    this.dispatcher.dispatch(clearReplyFormLocalErrors());

    this.dispatcher.dispatch(clearReviews());
    this.dialogRef.close({ submitted: false });
  }

  private bindStoreState(): void {
    // Use effects only for side-effects. Avoid assigning to computed signals here.
    effect(() => {
      // When generated comment arrives from store, patch it into the form asynchronously
      const gen = this.reviewsStore.generatedComment();
      if (gen && gen !== this.form.controls.reply.value) {
        Promise.resolve().then(() => this.form.controls.reply.setValue(gen));
      }

      // If the store reports an error, clear any success message
      const storeTemplateError = this.reviewsStore.templateError();
      if (storeTemplateError) {
        Promise.resolve().then(() => this.clearTemplateSuccessMessage());
      }

      // Keep template selection in sync with available templates and preferred id
      this.syncTemplateSelection();

      const templateMutationVersion = this.reviewsStore.templateMutationVersion();
      if (templateMutationVersion !== this.seenTemplateMutationVersion) {
        this.seenTemplateMutationVersion = templateMutationVersion;
        const lastMutatedTemplateId = this.reviewsStore.lastMutatedTemplateId();
        // schedule UI changes to next microtask to avoid ExpressionChangedAfterItHasBeenCheckedError
        Promise.resolve().then(() => {
          this.handleTemplateMutationSuccess(lastMutatedTemplateId);
          this.loadTemplates(lastMutatedTemplateId);
        });
      }

      const replySubmitVersion = this.reviewsStore.replySubmitVersion();
      if (replySubmitVersion !== this.seenReplySubmitVersion) {
        this.seenReplySubmitVersion = replySubmitVersion;
        Promise.resolve().then(() => this.dialogRef.close({ submitted: true }));
      }
    }, { injector: this.injector });
  }

  private syncTemplateSelection(): void {
    if (this.preferredTemplateId) {
      const preferredTemplateId = this.preferredTemplateId;
      this.preferredTemplateId = undefined;
      this.applyTemplateSelection(preferredTemplateId);
      return;
    }

    const currentTemplateId = this.form.controls.templateId.value;
    if (currentTemplateId) {
      this.applyTemplateSelection(currentTemplateId);
      return;
    }

    if (!this.templates().length) {
      this.form.controls.templateId.setValue('');
      if (this.activeTab === 'select') {
        this.templateMode = 'custom';
      }
    }
  }

  private applyTemplateSelection(templateId: string): void {
    const template = this.templates().find((item: AssetData) => item.id === templateId);
    if (!template) {
      if (this.activeTab === 'select') {
        this.templateMode = 'custom';
      } else if (this.templateMode === 'edit') {
        this.templateMode = 'create';
      }
      return;
    }

    this.form.controls.templateId.setValue(templateId);
    this.patchTemplateIntoForm(template);

    if (this.activeTab === 'select') {
      this.templateMode = 'selected';
    }
  }

  private patchTemplateIntoForm(template: AssetData): void {
    this.form.patchValue({
      title: template.name ?? '',
      reply: template.content ?? ''
    });
  }

  private isOwnTemplate(template?: AssetData): boolean {
    return !!template?.entityIds?.length;
  }

  private handleTemplateMutationSuccess(lastMutatedTemplateId?: string): void {
    this.activeTab = 'manage';

    if (this.pendingTemplateAction === 'delete') {
      this.templateMode = 'create';
      this.form.patchValue({ templateId: '', title: '' });
      this.showTemplateSuccessMessage('Template deleted.');
      this.pendingTemplateAction = undefined;
      return;
    }

    this.templateMode = lastMutatedTemplateId ? 'edit' : 'create';
    this.showTemplateSuccessMessage(this.pendingTemplateAction === 'update' ? 'Template updated.' : 'Template saved.');
    this.pendingTemplateAction = undefined;
  }

  private showTemplateSuccessMessage(message: string): void {
    this.templateSuccessMessage = message;

    if (this.successMessageTimeoutId) {
      clearTimeout(this.successMessageTimeoutId);
    }

    // Keep the feedback subtle and brief so it does not block editing.
    this.successMessageTimeoutId = setTimeout(() => {
      this.templateSuccessMessage = '';
      this.successMessageTimeoutId = undefined;
    }, 3000);
  }

  private clearTemplateSuccessMessage(): void {
    this.templateSuccessMessage = '';

    if (this.successMessageTimeoutId) {
      clearTimeout(this.successMessageTimeoutId);
      this.successMessageTimeoutId = undefined;
    }
  }

  protected generateWithAi(): void {
    if (!this.hasReviewComment) {
      return;
    }
    const reviewId = this.review.id?.trim();
    if (!reviewId) {
      this.dispatcher.dispatch(setReplyFormUi({ localGenerateCommentError: 'This review does not have a reply target id yet.' }));
      return;
    }
    this.dispatcher.dispatch(setReplyFormUi({ localGenerateCommentError: '' }));
    this.dispatcher.dispatch(generateComment({ reviewId }));
  }
}
